import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileText, Loader2 } from "lucide-react";
import jsPDF from "jspdf";

type Surface = "blueprint_pdf" | "trend_sanitize";

const SURFACES: { id: Surface; label: string }[] = [
  { id: "blueprint_pdf", label: "Blueprint PDF events" },
  { id: "trend_sanitize", label: "Trend-sanitize events" },
];

/** Truncates a hash/uuid so exports stay useful for correlation but not re-identifying. */
const short = (v: string | null | undefined, n = 10) => (v ? `${v.slice(0, n)}…` : "");

const csvCell = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const download = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Admin export of redacted audit trails for blueprint PDF downloads and
 * trend-sanitize events. No raw IPs, user agents, payload samples or referrers
 * ever leave the database — identifiers are truncated hashes only.
 */
export function SecurityAuditExportPanel() {
  const [surface, setSurface] = useState<Surface>("blueprint_pdf");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [limit, setLimit] = useState(1000);
  const [busy, setBusy] = useState<"csv" | "pdf" | null>(null);

  const fetchRows = async (): Promise<{ headers: string[]; rows: string[][] }> => {
    const fromIso = from ? new Date(from).toISOString() : null;
    const toIso = to ? new Date(to).toISOString() : null;
    const capped = Math.min(Math.max(limit, 1), 5000);

    if (surface === "blueprint_pdf") {
      let q = supabase
        .from("blueprint_share_events")
        .select("id, created_at, slug, event_type, user_id, ip_hash, device_hash, country")
        .in("event_type", ["pdf_download", "pdf_generate"])
        .order("created_at", { ascending: false })
        .limit(capped);
      if (fromIso) q = q.gte("created_at", fromIso);
      if (toIso) q = q.lte("created_at", toIso);
      const { data, error } = await q;
      if (error) throw error;
      return {
        headers: ["created_at", "slug", "event_type", "user_ref", "ip_ref", "device_ref", "country"],
        rows: (data ?? []).map((r) => [
          r.created_at,
          r.slug ?? "",
          r.event_type ?? "",
          short(r.user_id, 8),
          short(r.ip_hash),
          short(r.device_hash),
          r.country ?? "",
        ]),
      };
    }

    let q = supabase
      .from("trend_sanitize_events")
      .select(
        "id, created_at, field, action, reason_code, original_length, sanitized_length, source, fingerprint",
      )
      .order("created_at", { ascending: false })
      .limit(capped);
    if (fromIso) q = q.gte("created_at", fromIso);
    if (toIso) q = q.lte("created_at", toIso);
    const { data, error } = await q;
    if (error) throw error;
    return {
      headers: [
        "created_at",
        "field",
        "action",
        "reason_code",
        "original_length",
        "sanitized_length",
        "source",
        "fingerprint_ref",
      ],
      rows: (data ?? []).map((r) => [
        r.created_at,
        r.field ?? "",
        r.action ?? "",
        r.reason_code ?? "",
        String(r.original_length ?? ""),
        String(r.sanitized_length ?? ""),
        r.source ?? "",
        short(r.fingerprint, 12),
      ]),
    };
  };

  const logExport = async (format: "csv" | "pdf", rowCount: number) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;
    await supabase
      .from("admin_export_events")
      .insert({
        user_id: user.id,
        surface: `security_audit_${surface}`,
        format,
        filters: { from: from || null, to: to || null, limit },
        row_count: rowCount,
      })
      .then(() => undefined, () => undefined);
  };

  const stamp = () => new Date().toISOString().replace(/[:.]/g, "-");

  const exportCsv = async () => {
    setBusy("csv");
    try {
      const { headers, rows } = await fetchRows();
      const csv = [headers.join(","), ...rows.map((r) => r.map(csvCell).join(","))].join("\n");
      download(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${surface}-audit-${stamp()}.csv`);
      await logExport("csv", rows.length);
      toast.success(`Exported ${rows.length} redacted row(s)`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(null);
    }
  };

  const exportPdf = async () => {
    setBusy("pdf");
    try {
      const { headers, rows } = await fetchRows();
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const label = SURFACES.find((s) => s.id === surface)?.label ?? surface;
      doc.setFont("helvetica", "bold").setFontSize(14);
      doc.text(`Trends Discovery — ${label} (redacted)`, 32, 36);
      doc.setFont("helvetica", "normal").setFontSize(9);
      doc.text(
        `Generated ${new Date().toLocaleString()} · rows ${rows.length} · range ${from || "all"} → ${
          to || "now"
        }`,
        32,
        52,
      );
      doc.text(
        "Identifiers are truncated one-way hashes. No raw IPs, user agents or user-provided text are included.",
        32,
        66,
      );

      let y = 90;
      const colWidth = 760 / headers.length;
      doc.setFont("courier", "bold").setFontSize(8);
      headers.forEach((h, i) => doc.text(h.slice(0, 18), 32 + i * colWidth, y));
      doc.setFont("courier", "normal");
      y += 12;
      for (const row of rows) {
        if (y > 560) {
          doc.addPage();
          y = 48;
        }
        row.forEach((cell, i) => doc.text(String(cell).slice(0, 20), 32 + i * colWidth, y));
        y += 11;
      }
      doc.save(`${surface}-audit-${stamp()}.pdf`);
      await logExport("pdf", rows.length);
      toast.success(`Exported ${rows.length} redacted row(s)`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <div>
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <FileText className="size-4 text-primary" />
          Redacted audit log export
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          Download blueprint PDF download events or trend-sanitize events as CSV or PDF. Exports are
          redacted (truncated hashes only) and each export is recorded in the admin audit trail.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">Source</span>
          <Select value={surface} onValueChange={(v) => setSurface(v as Surface)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SURFACES.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">From</span>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 text-xs" />
        </label>
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">To</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 text-xs" />
        </label>
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">Max rows</span>
          <Input
            type="number"
            min={1}
            max={5000}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="h-8 text-xs"
          />
        </label>
      </div>

      <div className="flex gap-2">
        <Button size="sm" onClick={exportCsv} disabled={busy !== null}>
          {busy === "csv" ? <Loader2 className="size-3 animate-spin mr-1" /> : <Download className="size-3 mr-1" />}
          Export CSV
        </Button>
        <Button size="sm" variant="outline" onClick={exportPdf} disabled={busy !== null}>
          {busy === "pdf" ? <Loader2 className="size-3 animate-spin mr-1" /> : <FileText className="size-3 mr-1" />}
          Export PDF
        </Button>
      </div>
    </div>
  );
}
